import { FC, useCallback } from 'react';
import { Stack, Typography } from '@mui/material';
import moment from 'moment';
import Modal from '@/components/ui/Modal';
import CTAButton from '@/components/ui/CTAButton';
import useNotificationStore from '@/store/notifications';
import { AppNotification } from '@/types';

/**
 * Popup fuer KRITISCHE System-Meldungen an Admin/SA (Decision #207, Alex
 * 16.09.2026 — „Popup statt Mail").
 *
 * Quelle ist der Notification-Store: `kritischUngelesen` kommt mit jedem
 * unread-count-Poll (30 s) vom Server, also <= 30 s Latenz, kein neuer Kanal,
 * kein Websocket. Das Modal ist `persistent` — kein X, kein ESC, kein
 * Backdrop-Klick (dokumentierte DS-Abweichung, Task-Datei
 * docs/tasks/phase-0/admin-notifications-popup.md): es bleibt, bis JEDE
 * Meldung quittiert ist. Quittieren = gelesen stellen; eine Entwarnung
 * (RECOVERED) stellt die Meldung serverseitig gelesen, dann verschwindet sie
 * mit dem naechsten Poll von selbst.
 *
 * Routine-Meldungen erscheinen hier nie — die liegen still in der Glocke.
 */
const AdminAlarmModal: FC = () => {
  const { kritischUngelesen, quittieren } = useNotificationStore();

  const handleAlleQuittieren = useCallback(async () => {
    await Promise.all(kritischUngelesen.map((n) => quittieren(n.id)));
  }, [kritischUngelesen, quittieren]);

  if (kritischUngelesen.length === 0) return null;

  const anzahl = kritischUngelesen.length;

  return (
    <Modal
      open
      persistent
      onClose={() => undefined}
      icon="Error"
      title={anzahl === 1 ? 'Kritische Systemmeldung' : `${anzahl} kritische Systemmeldungen`}
      subtitle="Bleibt offen, bis jede Meldung quittiert ist."
      maxWidth="sm"
      fullWidth
    >
      <Stack gap={4} data-testid="admin-alarm-modal">
        {kritischUngelesen.map((n: AppNotification) => (
          <Stack key={n.id} gap={1} data-testid="admin-alarm-eintrag">
            <Typography variant="kartentitel" component="p" sx={{ color: 'error.main' }}>{n.title}</Typography>
            <Typography variant="wert" sx={{ whiteSpace: 'pre-line' }}>
              {n.message}
            </Typography>
            <Typography variant="klein">
              {String(n.data?.alarm_key ?? '')}
              {' · '}
              {moment(n.created_at).format('DD.MM.YYYY HH:mm')}
            </Typography>
            <Stack direction="row" justifyContent="flex-end">
              <CTAButton label="Quittieren" variant="outlined" onClick={() => quittieren(n.id)} autoWidth />
            </Stack>
          </Stack>
        ))}
        {anzahl > 1 && (
          <Stack direction="row" gap={2} justifyContent="flex-end">
            <CTAButton label="Alle quittieren" variant="secondary" onClick={handleAlleQuittieren} autoWidth />
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};

export default AdminAlarmModal;
